/* Copyright 2015 The MathWorks, Inc. */

/**
 * A utility that enables you to add drag and rearrange ability to the children of a DOM Node. This is currently used by
 * Quick Access Bar.
 */

define([
    "dijit/registry",
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/on",
    "dojo/mouse",
    "dojo/Evented",
    "dojo/Stateful",
    "dojo/dom-construct",
    "dojo/dom-geometry"
], function (registry, declare, lang, on, mouse, Evented, Stateful, domConstruct, domGeometry) {

    var VENDOR_PREFIXES = ["-webkit-", "-moz-", "-ms-", ""];
    var DRAG_THRESHOLD = 5;
    var DRAG_ORIENTATIONS = ["horizontal", "vertical"];

    return declare([Evented, Stateful], {
        containerNode: null,
        containerNodeWithinPopup: false,
        dragOrientation: "horizontal",
        scrollBuffer: 50,
        /**
         * @param args.containerNode - A DOM Node whose children need to be made draggable and sortable
         */
        constructor: function (args) {
            // Ensure the dojo/Stateful setters get called first
            this.postscript(args);

            if (this.containerNode === undefined || this.containerNode.nodeType !== 1) {
                throw new Error("A containerNode needs to be provided and it should be a DOM Node!");
            }

            // Maintains the node currently being dragged
            this._currentTarget = null;

            // Used to determine the direction of drag
            this._previousPageX = 0;
            this._previousPageY = 0;

            this.get("containerNode").classList.add("mwDragSortContainer");
            this._dragSortOverlay = domConstruct.create("div", {"class": "mwDragSortContainerOverlay"}, document.body);

            this._mouseDownListenerHandle = on(this.get("containerNode"), "mousedown", lang.hitch(this, this._mouseDownListener));
            this._mouseUpListenerHandle = on(window, "mouseup", lang.hitch(this, this._mouseUpListener));
        },

        _setContainerNodeAttr: function (value) {
            if (args.containerNode === undefined || args.containerNode.nodeType !== 1) {
                throw new Error("A containerNode needs to be provided and it should be a DOM Node!");
            }
            this._set("containerNode", value);
        },

        _setDragOrientationAttr: function (value) {
            if ((typeof value !== "string") || (DRAG_ORIENTATIONS.indexOf(value) < 0)) {
                throw new Error("'dragOrientation' setter expected one of: [" + DRAG_ORIENTATIONS.toString() + "]");
            }
            this._set("dragOrientation", value);
        },

        _mouseDownListener: function (e) {
            // Only support left-click drag
            if (mouse.isLeft(e)) {
                // We want the current target to be a direct child of the container node, since the user can click on
                // any nested node within the children of the container node, we need to walk up to find the actual
                // direct child of the container which would be the one that drags around
                this._currentTarget = this._getAncestorWhichIsADirectChildOfContainerNode(e.target);

                //If the mouse down target is an immediate child of the container node and there are more than 1 children
                if (this._currentTarget !== null && this.get("containerNode").children.length > 1) {
                    var startingPosition = domGeometry.position(this._currentTarget);
                    if (!this._scrollNode) {
                        this._scrollNode = this._getContainingScrollNode(this.containerNode);
                    }
                    this._scrollNodeBoundingClientRect = this._scrollNode.getBoundingClientRect();
                    this._mouseMoveListenerHandle = on(document.body, "mousemove",
                        lang.hitch(this, this._mouseMoveListener, {
                            index: this._getIndexOfNode(this._currentTarget),
                            position: startingPosition,
                            leftOffset: e.pageX - startingPosition.x,
                            rightOffset: startingPosition.x + startingPosition.w - e.pageX,
                            topOffset: e.pageY - startingPosition.y,
                            bottomOffset: startingPosition.y + startingPosition.h - e.pageY,
                            mouseDownCoords: {
                                x: e.pageX,
                                y: e.pageY
                            },
                            startScrollLeft: this._scrollNode.scrollLeft,
                            startScrollTop: this._scrollNode.scrollTop
                        }));
                    this._windowSelectStartHandle = on(window, "selectstart", function (e) {
                        e.preventDefault();
                    });

                }
            }
        },

        _getScrollableDirection: function (node) {
            var direction = "none",
                computedStyles = window.getComputedStyle(node),
                scrollableX = (["scroll", "auto"].indexOf(computedStyles['overflow-x']) > -1) && (node.scrollWidth > node.clientWidth),
                scrollableY = (["scroll", "auto"].indexOf(computedStyles['overflow-y']) > -1) && (node.scrollHeight > node.clientHeight);
            // May be able to simplify this check with node.offsetWidth vs node.clientWidth, but then the scrollWidth is confusing

            if (scrollableX && scrollableY) {
                direction = "both";
            } else if (scrollableX) {
                direction = "x";
            } else if (scrollableY) {
                direction = "y";
            }
            return direction;
        },

        _getContainingScrollNode: function (node) {
            var scrollNode = null;
            while (document.body.contains(node)) {
                if (this._getScrollableDirection(node) !== "none") {
                    scrollNode = node;
                    break;
                }
                node = node.parentElement;
            }
            return scrollNode || this.containerNode;
        },

        _autoScrollContainer: (function () {
            var shouldScroll = false,
                supportedDirections = [
                    "up",
                    "u",
                    "U",
                    "down",
                    "d",
                    "D",
                    "left",
                    "l",
                    "L",
                    "right",
                    "r",
                    "R",
                    "stop",
                    "none",
                    null,
                    undefined
                ],
                x = 0,
                y = 0,
                scrollNode = null,
                currReq = null,
                scrollContainer = function () {
                    currReq = null;
                    if (scrollNode && shouldScroll) {
                        scrollNode.scrollBy(x, y);
                        currReq = window.requestAnimationFrame(scrollContainer);
                    }
                };
            return function (container, direction, step) {
                if (currReq) {
                    window.cancelAnimationFrame(currReq);
                }
                if (this._getScrollableDirection(container) === "none") {
                    direction = "stop";
                    return;
                }

                scrollNode = container;

                if (supportedDirections.indexOf(direction) < 0) {
                    direction = "down"
                }
                if (!step || (typeof step !== "number")) {
                    step = 1;
                }
                switch (direction) {
                    case "stop":
                    case "none":
                    case null:
                    case undefined:
                        shouldScroll = false;
                        break;
                    case "left":
                    case "l":
                    case "L":
                        shouldScroll = true;
                        x = -step;
                        y = 0;
                        currReq = window.requestAnimationFrame(scrollContainer);
                        break;
                    case "right":
                    case "r":
                    case "R":
                        shouldScroll = true;
                        x = step;
                        y = 0;
                        currReq = window.requestAnimationFrame(scrollContainer);
                        break;
                    case "up":
                    case "u":
                    case "U":
                        shouldScroll = true;
                        x = 0;
                        y = -step;
                        currReq = window.requestAnimationFrame(scrollContainer);
                        break;
                    case "down":
                    case "d":
                    case "D":
                    default:
                        shouldScroll = true;
                        x = 0;
                        y = step;
                        currReq = window.requestAnimationFrame(scrollContainer);
                        break;
                }
            };
        })(),

        _mouseMoveListener: function (startInfo, e) {
            if (Math.abs(startInfo.mouseDownCoords.x - e.pageX) <= DRAG_THRESHOLD
                && Math.abs(startInfo.mouseDownCoords.y - e.pageY) <= DRAG_THRESHOLD) {
                return;
            }

            this._mouseMoveHappened = true;

            this._currentTarget.classList.add("mwDragTarget");
            // Mark the body to be in dragging state to show the move cursor
            document.body.classList.add("mwDragging");

            this._adjustDraggableNodePostions(startInfo, e);

            var scrollStep = (this._scrollGap < 0) ? Math.ceil(Math.abs(this._scrollGap)/5) : 1;
            if (this._scrollListenerHandle) {
                this._scrollListenerHandle.remove();
                delete this._scrollListenerHandle;
            }
            if (this._scrollGap < this.scrollBuffer) {
                this._autoScrollContainer(this._scrollNode, this._dragDirection, scrollStep);

                this._scrollListenerHandle = on(this._scrollNode, "scroll",
                    function () {
                        this._adjustDraggableNodePostions(startInfo, e);
                    }.bind(this)
                );
            } else {
                this._autoScrollContainer(this._scrollNode, "stop");
            }

            if (this._currentTarget && this._currentTarget.firstElementChild) {
                var targetWidget = registry.getEnclosingWidget(this._currentTarget.firstElementChild);
                if (targetWidget) {
                    // If the targetWidget has a menu which is opened, close it.
                    if (targetWidget.isMenuOpen && targetWidget.isMenuOpen()) {
                        if (targetWidget.closeMenu) {
                            targetWidget.closeMenu();
                        }
                    }
                    if (this.containerNodeWithinPopup) {
                        // QAB Overflow popup use case: If the target is within a popup, save the parent menu to focus it
                        // after the target is dropped. Not doing this will get the UI in a state where no element is focused
                        // when the target is dropped. So, the next drag would fire menu's mousedown callback which closes
                        // the menu if nothing is focused treating as focus change.
                        this._parentMenuToBeFocused = targetWidget.getParent ? targetWidget.getParent() : null;
                    }
                }
            }
        },

        _adjustDraggableNodePostions: function (startInfo, e) {
            var lowerBound, upperBound, nodes, child, childPosition, currentTargetPosition, i, scrollDiff;
            var containerPosition = domGeometry.position(this.get("containerNode"));
            var containerPadBorderExtents = domGeometry.getPadBorderExtents(this.get("containerNode"));

            if (this.get("dragOrientation") === "horizontal") {
                lowerBound = containerPosition.x + startInfo.leftOffset + containerPadBorderExtents.l;
                upperBound = containerPosition.x + containerPosition.w - startInfo.rightOffset - containerPadBorderExtents.r;
                this._currentPageX = e.pageX;
                scrollDiff = this._scrollNode.scrollLeft - startInfo.startScrollLeft;

                // Limit the current target to be draggable only within the content box of the container node
                if (this._currentPageX < lowerBound) {
                    this._currentPageX = lowerBound;
                } else if (this._currentPageX > upperBound) {
                    this._currentPageX = upperBound;
                }

                // Move the current target along with the mouse position
                this._currentTarget.style.left = this._currentPageX - startInfo.position.x - startInfo.leftOffset + scrollDiff + "px";

                // Determine the current drag direction based upon the previous mouse position
                if (e.pageX > this._previousPageX) {
                    this._dragDirection = "r";
                    this._scrollGap = (this._scrollNodeBoundingClientRect.left + this._scrollNodeBoundingClientRect.width) - e.pageX;
                } else if (e.pageX < this._previousPageX) {
                    this._dragDirection = "l";
                    this._scrollGap = e.pageX - this._scrollNodeBoundingClientRect.left;
                } else {
                    if (!this._dragDirection || !this._scrollGap) {
                        return;
                    }
                }
                this._previousPageX = e.pageX;

                // Find the siblings of the current target on left and right side of the page based on the current position
                nodes = this._findNodesOnEitherSide(this._dragDirection);

                // Translate each sibling to the correct location based on the position of the current target
                for (i = 0; i < this.get("containerNode").children.length; i++) {
                    child = this.get("containerNode").children[i];

                    // Skip the current target itself since it is already moving with the mouse position
                    if (child !== this._currentTarget) {
                        childPosition = domGeometry.position(child);
                        currentTargetPosition = domGeometry.position(this._currentTarget);

                        if (this._dragDirection === "r") {
                            if (i >= startInfo.index) {
                                if ((currentTargetPosition.x + currentTargetPosition.w) >= (childPosition.x + childPosition.w / 2)) {
                                    this._applyTranslateX(child, -currentTargetPosition.w);
                                }
                            } else if (nodes.left.indexOf(child) > -1 && child.getAttribute("data-translated") !== "0") {
                                this._applyTranslateX(child, 0);
                            }
                        } else if (this._dragDirection === "l") {
                            if (i <= startInfo.index) {
                                if ((currentTargetPosition.x) <= (childPosition.x + childPosition.w / 2)) {
                                    this._applyTranslateX(child, currentTargetPosition.w);
                                }
                            } else if (nodes.right.indexOf(child) > -1 && child.getAttribute("data-translated") !== "0") {
                                this._applyTranslateX(child, 0);
                            }
                        }
                    }

                }
            } else if (this.get("dragOrientation") === "vertical") {
                lowerBound = containerPosition.y + startInfo.topOffset + containerPadBorderExtents.t;
                upperBound = containerPosition.y + containerPosition.h - startInfo.bottomOffset - containerPadBorderExtents.b;
                this._currentPageY = e.pageY;
                scrollDiff = this._scrollNode.scrollTop - startInfo.startScrollTop;

                // Limit the current target to be draggable only within the content box of the container node
                if (this._currentPageY < lowerBound) {
                    this._currentPageY = lowerBound;
                } else if (this._currentPageY > upperBound) {
                    this._currentPageY = upperBound;
                }

                // Move the current target along with the mouse position
                this._currentTarget.style.top = this._currentPageY - startInfo.position.y - startInfo.topOffset + scrollDiff + "px";

                // Determine the current drag direction based upon the previous mouse position
                if (e.pageY > this._previousPageY) {
                    this._dragDirection = "d";
                    this._scrollGap = (this._scrollNodeBoundingClientRect.top + this._scrollNodeBoundingClientRect.height) - e.pageY;
                } else if (e.pageY < this._previousPageY) {
                    this._dragDirection = "u";
                    this._scrollGap = e.pageY - this._scrollNodeBoundingClientRect.top;
                } else {
                    if (!this._dragDirection || !this._scrollGap) {
                        return;
                    }
                }
                this._previousPageY = e.pageY;

                // Find the siblings of the current target on left and right side of the page based on the current position
                nodes = this._findNodesAboveAndBelow(this._dragDirection);

                // Translate each sibling to the correct location based on the position of the current target
                for (i = 0; i < this.get("containerNode").children.length; i++) {
                    child = this.get("containerNode").children[i];

                    // Skip the current target itself since it is already moving with the mouse position
                    if (child !== this._currentTarget) {
                        childPosition = domGeometry.position(child);
                        currentTargetPosition = domGeometry.position(this._currentTarget);

                        if (this._dragDirection === "d") {
                            if (i >= startInfo.index) {
                                if ((currentTargetPosition.y + currentTargetPosition.h) >= (childPosition.y + childPosition.h / 2)) {
                                    this._applyTranslateY(child, -currentTargetPosition.h);
                                }
                            } else if (nodes.above.indexOf(child) > -1 && child.getAttribute("data-translated") !== "0") {
                                this._applyTranslateY(child, 0);
                            }
                        } else if (this._dragDirection === "u") {
                            if (i <= startInfo.index) {
                                if ((currentTargetPosition.y) <= (childPosition.y + childPosition.h / 2)) {
                                    this._applyTranslateY(child, currentTargetPosition.h);
                                }
                            } else if (nodes.below.indexOf(child) > -1 && child.getAttribute("data-translated") !== "0") {
                                this._applyTranslateY(child, 0);
                            }
                        }
                    }

                }
            }
        },

        _mouseUpListener: function () {
            if (this._scrollListenerHandle) {
                this._scrollListenerHandle.remove();
                delete this._scrollListenerHandle;
            }

            if (this._scrollNode) {
                this._autoScrollContainer(this._scrollNode, "stop");
            }

            if (this._scrollGap) {
                delete this._scrollGap;
            }

            if (this._dragDirection) {
                delete this._dragDirection;
            }

            if (this._mouseMoveListenerHandle) {
                this._mouseMoveListenerHandle.remove();
                delete this._mouseMoveListenerHandle;
            }

            if (this._windowSelectStartHandle) {
                this._windowSelectStartHandle.remove();
                delete this._windowSelectStartHandle;
            }

            if (this._currentPageX) {
                delete this._currentPageX;
            }

            if (this._currentPageY) {
                delete this._currentPageY;
            }

            if (!this._mouseMoveHappened) {
                return;
            }

            if (this._currentTarget !== null) {
                var currentTargetOldIndex = this._getIndexOfNode(this._currentTarget);

                this._moveCurrentTargetToNewLocation();

                if (this.get("dragOrientation") === "horizontal") {
                    this._currentTarget.style.left = "";
                    this._resetAllSiblingTranslateX();
                } else {
                    this._currentTarget.style.top = "";
                    this._resetAllSiblingTranslateY();
                }

                this._currentTarget.classList.remove("mwDragTarget");
                document.body.classList.remove("mwDragging");

                this.emit("nodemoved", {
                    node: this._currentTarget,
                    oldIndex: currentTargetOldIndex,
                    newIndex: this._getIndexOfNode(this._currentTarget)
                });

                this._currentTarget = null;

                if (this.containerNodeWithinPopup && this._parentMenuToBeFocused && this._parentMenuToBeFocused.focusNode) {
                    // Focus the parent menu to avoid it from getting closed in the next drag.
                    this._parentMenuToBeFocused.focusNode.focus();
                    delete this._parentMenuToBeFocused;
                }
            }

            this._mouseMoveHappened = false;
        },

        _findNodesOnEitherSide: function (dragDirection) {
            var nodes = {
                left: [],
                right: []
            };

            var dragSorterContainerNodePos = domGeometry.position(this.get("containerNode"));
            var containerPadBorderExtents = domGeometry.getPadBorderExtents(this.get("containerNode"));
            var currentTargetPos = domGeometry.position(this._currentTarget);

            // If the current target is very close to the left edge of the container then all the siblings should be
            // translated to the right
            if (currentTargetPos.x - dragSorterContainerNodePos.x - containerPadBorderExtents.l <= 2) {

                nodes.right = this._getCurrentTargetSiblings();

                // If the current target is very close to the right edge of the container then all the siblings should
                // be translated to the left
            } else if ((dragSorterContainerNodePos.x + dragSorterContainerNodePos.w - currentTargetPos.x - currentTargetPos.w - containerPadBorderExtents.l) <= 2) {

                nodes.left = this._getCurrentTargetSiblings();

            } else {
                this._getCurrentTargetSiblings().forEach(function (child) {
                    var childPos = domGeometry.position(child);

                    var comparator = dragDirection === "l" ? currentTargetPos.x : currentTargetPos.x + currentTargetPos.w;
                    if (comparator >= (childPos.x + childPos.w / 2)) {
                        nodes.left.push(child);
                    } else {
                        nodes.right.push(child);
                    }
                });
            }

            return nodes;
        },

        _findNodesAboveAndBelow: function (dragDirection) {
            var nodes = {
                above: [],
                below: []
            };

            var dragSorterContainerNodePos = domGeometry.position(this.get("containerNode"));
            var containerPadBorderExtents = domGeometry.getPadBorderExtents(this.get("containerNode"));
            var currentTargetPos = domGeometry.position(this._currentTarget);

            // If the current target is very close to the top edge of the container then all the siblings should be
            // translated to the down
            if (currentTargetPos.y - dragSorterContainerNodePos.y - containerPadBorderExtents.t <= 2) {

                nodes.below = this._getCurrentTargetSiblings();

                // If the current target is very close to the bottom edge of the container then all the siblings should
                // be translated to the up
            } else if ((dragSorterContainerNodePos.y + dragSorterContainerNodePos.h - currentTargetPos.y - currentTargetPos.h - containerPadBorderExtents.t) <= 2) {

                nodes.above = this._getCurrentTargetSiblings();

            } else {
                this._getCurrentTargetSiblings().forEach(function (child) {
                    var childPos = domGeometry.position(child);

                    var comparator = dragDirection === "u" ? currentTargetPos.y : currentTargetPos.y + currentTargetPos.h;
                    if (comparator >= (childPos.y + childPos.h / 2)) {
                        nodes.above.push(child);
                    } else {
                        nodes.below.push(child);
                    }
                });
            }

            return nodes;
        },

        _getIndexOfNode: function (node) {
            return Array.prototype.indexOf.call(this.get("containerNode").children, node);
        },

        _getCurrentTargetSiblings: function () {
            return [].slice.call(this.get("containerNode").children).filter(function (child) {
                return child !== this._currentTarget;
            }, this);
        },

        // Given a node, find the ancestor who is a direct child of the dragSortContainerNode.
        _getAncestorWhichIsADirectChildOfContainerNode: function (node) {
            var targetChild = null;
            if (node !== this.get("containerNode")) {
                targetChild = node;
                while (targetChild.parentNode !== this.get("containerNode")) {
                    targetChild = targetChild.parentNode;
                }
            }
            return targetChild;
        },

        _moveCurrentTargetToNewLocation: function () {
            var afterNode = null;
            var containerPosition = domGeometry.position(this.get("containerNode"));
            var containerPadBorderExtents = domGeometry.getPadBorderExtents(this.get("containerNode"));
            var currentTargetPos = domGeometry.position(this._currentTarget);

            var minDistance;
            if (this.get("dragOrientation") === "horizontal") {
                minDistance = currentTargetPos.x - (containerPosition.x + containerPadBorderExtents.l);
            } else {
                minDistance = currentTargetPos.y - (containerPosition.y + containerPadBorderExtents.t);
            }

            var initialMinDistance = minDistance;

            // Find the closest sibling after which the current target node should be placed
            this._getCurrentTargetSiblings().forEach(function (sibling) {
                var siblingPos = domGeometry.position(sibling);

                var diff;
                if (this.get("dragOrientation") === "horizontal") {
                    diff = Math.abs(currentTargetPos.x - (siblingPos.x + siblingPos.w));
                } else {
                    diff = Math.abs(currentTargetPos.y - (siblingPos.y + siblingPos.h));
                }

                if (diff < minDistance) {
                    afterNode = sibling;
                    minDistance = diff;
                }
            }.bind(this));

            // If no other sibling was able to update the minDistance then the current target would be closest to the
            // left edge of the container itself, so place it as the first child
            if (minDistance === initialMinDistance) {
                domConstruct.place(this._currentTarget, this.get("containerNode"), "first");
            } else {
                domConstruct.place(this._currentTarget, afterNode, "after");
            }
        },

        _applyTranslateX: function (node, x) {
            node.setAttribute("data-translated", x);
            VENDOR_PREFIXES.forEach(function (prefix) {
                node.style[prefix + "transform"] = "translate(" + x + "px, 0)";
            });
        },

        _applyTranslateY: function (node, y) {
            node.setAttribute("data-translated", y);
            VENDOR_PREFIXES.forEach(function (prefix) {
                node.style[prefix + "transform"] = "translate(0, " + y + "px)";
            });
        },

        _resetAllSiblingTranslateX: function () {
            this._getCurrentTargetSiblings().forEach(function (child) {
                child.classList.add("mwNoTransition");
                this._applyTranslateX(child, 0);

                // Force a paint for the above class addition to take effect                
                child.offsetHeight;

                child.classList.remove("mwNoTransition");
            }, this);
        },

        _resetAllSiblingTranslateY: function () {
            this._getCurrentTargetSiblings().forEach(function (child) {
                child.classList.add("mwNoTransition");
                this._applyTranslateY(child, 0);

                // Force a paint for the above class addition to take effect
                child.offsetHeight;

                child.classList.remove("mwNoTransition");
            }, this);
        },

        /**
         * Cleans up any event listeners and classes added to the containerNode
         */
        destroy: function () {
            if (this._scrollListenerHandle) {
                this._scrollListenerHandle.remove();
            }
            this._mouseDownListenerHandle.remove();
            this._mouseUpListenerHandle.remove();
            this.get("containerNode").classList.remove("mwDragSortContainer");
            domConstruct.destroy(this._dragSortOverlay);
        }
    });
});